import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { Alert, Button, Modal } from 'react-bootstrap';
import Select from 'react-select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarDays, faCircleCheck, faCreditCard, faFingerprint, faUser } from '@fortawesome/free-solid-svg-icons';
import { options } from '../../actions/webServices';
import { isNaranjaCard, validateCardFields } from '../../utilities';
import SpinnerLoading from '../SpinnerLoading';
import './styles.css';

/**
 * Formulario de tarjeta para el flujo de venta telefónica.
 * No usa Redux ni react-router: es local a ConfirmBuy y se muestra únicamente
 * después de que el cliente confirma la contratación en una sucursal de venta
 * telefónica (ver `isPhoneSaleSucursal`).
 *
 * Este componente arma su propio `URLSearchParams` por request y jamás reutiliza
 * el `params` singleton exportado por `actions/webServices.js`: ese objeto es
 * compartido/mutado por toda la app para llamadas a `getpm`, y este formulario
 * envía datos de tarjeta a `acceptpm` (vía la prop `url`).
 */
function PhoneSaleCardForm({ codpmpol, url, producto, pago, onSuccess }) {
    const [cards, setCards] = useState([]);
    const [mensaje, setMensaje] = useState(null);
    const [error, setError] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [form, setForm] = useState({
        codtar: '',
        cardNumber: '',
        fullName: '',
        dni: '',
        expires: '',
    });

    useEffect(() => {
        const p = new URLSearchParams();
        p.set('opcpm', 'tarjetasVentaTelefonica');
        p.set('codpmpol', codpmpol);
        axios.post(url, p, options)
            .then((r) => {
                const opts = [];
                r.data?.forEach((item) => {
                    opts.push({
                        value: item.codtar,
                        label: item.nomtar,
                    });
                });
                setCards(opts);
            })
            .catch(() => setCards([]));
    }, [codpmpol, url]);

    const selectedCard = cards.find((c) => c.value === form.codtar) || null;

    const normalizeCC = (e) => {
        const value = e.target.value.replace(/\s/g, '').match(/.{1,4}/g)?.join(' ').substr(0, 19) || '';
        setForm((f) => ({ ...f, cardNumber: value }));
    };

    const normalizeDate = (e) => {
        const value = e.target.value.replace(/\D/g, '').replace(/(\d{2})(\d)/, '$1/$2').replace(/(\d{2})(\d)/, '$1/$2');
        setForm((f) => ({ ...f, expires: value }));
    };

    const normalizeDNI = (e) => {
        const value = e.target.value.replace(/\D/g, '').replace(/(\d{2})(\d)/, '$1.$2').replace(/(\d{3})(\d)/, '$1.$2');
        setForm((f) => ({ ...f, dni: value }));
    };

    const verifyForm = () => {
        const { cardNumber, codtar, dni, expires, fullName } = form;
        // `BINs` está indexado por `cod-tar` (el código), no por el nombre visible.
        const cardError = validateCardFields({ cardNumber, creditCard: codtar, fullName, dni, expires });
        if (cardError) {
            setMensaje(cardError);
            return false;
        }
        return true;
    };

    /**
     * Chequeo de BIN contra `acceptpm` (la prop `url`), con su propio URLSearchParams.
     * No manda `sec`: el back lo deduce de la póliza.
     * Devuelve el mensaje a mostrar, o null si la tarjeta es de crédito. Si la llamada
     * falla también bloquea: no se puede dar por buena una tarjeta que no se validó.
     */
    const validateTypeCard = async (cardNumber) => {
        const p = new URLSearchParams();
        p.set('opcpm', 'binChecker');
        p.set('codpmpol', codpmpol);
        p.set('bin', cardNumber.replaceAll(' ', '').substring(0, 6));
        return axios.post(url, p, options)
            .then((r) => (r?.data?.isCredit ? null : 'Revise la tarjeta, solo se acepta crédito'))
            .catch(() => 'No pudimos validar su tarjeta. Intente nuevamente.');
    };

    const handleSubmit = async () => {
        setError(false);
        if (!verifyForm()) {
            setError(true);
            return;
        }
        if (!isNaranjaCard(form.cardNumber)) { // Si no es Naranja
            setSubmitting(true);
            const cardTypeError = await validateTypeCard(form.cardNumber);
            setSubmitting(false);
            if (cardTypeError) {
                setMensaje(cardTypeError);
                setError(true);
                return;
            }
        }
        setShowConfirm(true);
    };

    const confirmSubmit = () => {
        setShowConfirm(false);
        setSubmitting(true);
        const strippedCardNumber = form.cardNumber.replaceAll(' ', '');
        const p = new URLSearchParams();
        p.set('opcpm', 'confirmaVentaTelefonica');
        p.set('codpmpol', codpmpol);
        p.set('codpag', '1');
        p.set('codtar', form.codtar);
        p.set('tnumpol', strippedCardNumber);
        p.set('ul4pol', strippedCardNumber.slice(-4));
        p.set('tnompol', form.fullName);
        p.set('tdnipol', form.dni.replaceAll('.', ''));
        p.set('tvtopol', form.expires.replaceAll('/', ''));
        axios.post(url, p, options)
            .then((r) => {
                setSubmitting(false);
                if (r?.data?.codpmpol === 'ok') {
                    onSuccess();
                } else {
                    setMensaje(r?.data?.error || 'Ocurrió un error al confirmar el pago. Intente nuevamente.');
                    setError(true);
                }
            })
            .catch(() => {
                setSubmitting(false);
                setMensaje('Ocurrió un error al confirmar el pago. Intente nuevamente.');
                setError(true);
            });
    };

    return (
        <div className="phone-sale-card-form qs-inputs mb-4 custom-border-2 m-auto confirm">
            <h2 className="text-start mb-3">Completá los datos de tu tarjeta</h2>
            {producto && <p className="fw-semibold color-title-blue mb-1">Seguro de {producto}</p>}
            {pago && <p className="fw-semibold color-title-blue mb-3">Pago por mes: {pago}</p>}
            <div className="phone-sale-card-form-fields">
                <h3 className="color-title-blue">Tarjeta</h3>
                <Select
                    options={cards}
                    value={selectedCard}
                    onChange={(opt) => setForm((f) => ({ ...f, codtar: opt?.value || '' }))}
                    placeholder=""
                    theme={(theme) => ({
                        ...theme,
                        colors: { ...theme.colors, neutral50: '#163275' },
                    })}
                />
                <h3 className="color-title-blue">Número de tarjeta</h3>
                <div className="d-flex flex-nowrap input-icon m-0">
                    <span className="input-group-text change-input"><FontAwesomeIcon icon={faCreditCard} /></span>
                    <input
                        className="input-field"
                        type="tel"
                        value={form.cardNumber}
                        onChange={normalizeCC}
                        autoComplete="off"
                    />
                </div>
                <h3 className="color-title-blue">Nombre y Apellido</h3>
                <div className="d-flex flex-nowrap input-icon m-0">
                    <span className="input-group-text change-input"><FontAwesomeIcon icon={faUser} /></span>
                    <input
                        className="input-field"
                        type="text"
                        value={form.fullName}
                        onChange={(e) => setForm((f) => ({ ...f, fullName: e.target.value }))}
                        autoComplete="off"
                    />
                </div>
                <h3 className="color-title-blue">DNI</h3>
                <div className="d-flex flex-nowrap input-icon m-0">
                    <span className="input-group-text change-input"><FontAwesomeIcon icon={faFingerprint} /></span>
                    <input
                        className="input-field"
                        type="text"
                        maxLength={10}
                        value={form.dni}
                        onChange={normalizeDNI}
                        autoComplete="off"
                    />
                </div>
                <h3 className="color-title-blue">Fecha de vencimiento</h3>
                <div className="d-flex flex-nowrap input-icon m-0">
                    <span className="input-group-text change-input"><FontAwesomeIcon icon={faCalendarDays} /></span>
                    <input
                        className="input-field"
                        type="text"
                        maxLength={5}
                        value={form.expires}
                        onChange={normalizeDate}
                        autoComplete="off"
                    />
                </div>
            </div>
            {
                error &&
                <Alert className="mt-3" variant="danger">
                    {mensaje}
                </Alert>
            }
            {
                submitting
                    ? <SpinnerLoading />
                    : <Button onClick={handleSubmit} className="main-btn mt-3">Confirmar pago</Button>
            }
            <Modal show={showConfirm} onHide={() => setShowConfirm(false)}>
                <Modal.Header>
                    <Modal.Title><FontAwesomeIcon icon={faCircleCheck} /> ¡Aviso!</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>
                        ¿Confirma que los datos de la tarjeta son correctos?
                        Se registrará el pago con la tarjeta {selectedCard?.label} terminada
                        en {form.cardNumber.replaceAll(' ', '').slice(-4)}.
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button onClick={() => setShowConfirm(false)} className="main-btn">
                        Revisar datos
                    </Button>
                    <Button variant="success" onClick={confirmSubmit}>
                        Sí, confirmar
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}

export default PhoneSaleCardForm;
